<template>
  <div class="dialogue">
    <div class="greeting-area">
      <GreetingArea isShowRgt></GreetingArea>
    </div>
    <div class="dialogue-area" ref="scrollableContainer">
      <AIChatting
        v-for="(item, index) in treatmentList"
        :key="`${item.type}-${index}-${item.id || ''}`"
        :cardData="item"
        hideInlineConfirm
        @sendUserConfirmMsg="sendUserConfirmMsg"
        @taskCompleted="continueWorkflowAfterTask({ forceAdvance: true })"
      ></AIChatting>
    </div>
    <div class="input-area">
      <transition name="quick-reply">
        <button
          v-if="activeWorkflowPrompt"
          class="workflow-reply"
          :disabled="interactionBusy"
          @click="sendWorkflowReply"
        >
          <span class="workflow-reply-label">推荐回复</span>
          <span class="workflow-reply-text">
            {{ workflowAdvancing ? '正在继续...' : activeWorkflowPrompt.text }}
          </span>
          <span class="workflow-reply-arrow" aria-hidden="true">›</span>
        </button>
        <div v-else-if="hasIncompleteTask" class="workflow-reminder">
          卡片会为你保留。你可以先聊聊，准备好后再点击完成
        </div>
      </transition>
      <div class="composer-row">
        <textarea
          v-model="inputText"
          class="input-box"
          :placeholder="inputPlaceholder"
          :disabled="agentSending"
          rows="1"
        ></textarea>
        <button class="send-btn" :disabled="interactionBusy || !trimmedInput" @click="sendText">
          {{ agentSending ? '发送中' : '发送' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { v4 as uuidv4 } from "uuid";
import GreetingArea from "@/components/GreetingArea/GreetingArea.vue";
import AIChatting from "@/components/AIChatting/AIChatting.vue";
import { streamAgent } from "@/api/agent";
import { clearDialogueTaskEntry } from "@/utils/taskNavigation";

export default {
  name: "Dialogue",
  components: {
    GreetingArea,
    AIChatting,
  },
  data() {
    return {
      conversationId: uuidv4(),
      inputText: "",
      inputPlaceholder: "请输入你的想法，建议使用语音输入法",
      agentSending: false,
      workflowAdvancing: false,
      initialized: false,
    };
  },
  computed: {
    ...mapState({
      treatmentList: (state) => state.dialogue.treatmentList,
      treatmentCode: (state) => state.dialogue.treatmentCode,
      treatmentInstanceId: (state) => state.dialogue.id,
    }),
    activeWorkflowPrompt() {
      const prompt = this.latestWorkflowPrompt;
      return prompt && !this.hasIncompleteTask ? prompt : null;
    },
    latestWorkflowPrompt() {
      for (let index = this.treatmentList.length - 1; index >= 0; index -= 1) {
        const item = this.treatmentList[index];
        const settings = item && item.settings;
        if (
          item &&
          item.item_type === "guide_language" &&
          settings &&
          settings.textChange &&
          item.status !== "COMPLETED"
        ) {
          return {
            id: item.id,
            text: settings.textSuccess || "继续",
            source: item,
            index,
          };
        }
      }
      return null;
    },
    hasIncompleteTask() {
      const prompt = this.latestWorkflowPrompt;
      const endIndex = prompt ? prompt.index : this.treatmentList.length;
      let startIndex = 0;
      for (let index = endIndex - 1; index >= 0; index -= 1) {
        const item = this.treatmentList[index];
        if (
          item &&
          item.item_type === "guide_language" &&
          item.settings &&
          item.settings.textChange &&
          item.status === "COMPLETED"
        ) {
          startIndex = index + 1;
          break;
        }
      }
      return this.treatmentList.slice(startIndex, endIndex).some((item) => (
        item &&
        item.item_type &&
        item.item_type !== "guide_language" &&
        item.item_type !== "user_input" &&
        item.status !== "COMPLETED"
      ));
    },
    incompleteTaskIds() {
      return this.treatmentList
        .filter((item) => (
          item &&
          item.id != null &&
          item.item_type &&
          item.item_type !== "guide_language" &&
          item.item_type !== "user_input" &&
          item.status !== "COMPLETED"
        ))
        .map((item) => String(item.id));
    },
    trimmedInput() {
      return this.inputText.trim();
    },
    interactionBusy() {
      return this.agentSending || this.workflowAdvancing;
    },
  },
  mounted() {
    clearDialogueTaskEntry();
    this.initTreatmentFn();
  },
  activated() {
    clearDialogueTaskEntry();
    if (this.initialized) {
      this.continueWorkflowAfterTask();
    }
  },
  methods: {
    ...mapActions([
      "initTreatment",
      "getTreatment",
      "getTreatmentNext",
      "postUserConfirm",
    ]),
    async initTreatmentFn() {
      const resData = await this.initTreatment();
      if (resData) {
        const history = await this.getTreatmentFn();
        this.initialized = true;
        // A task page may be refreshed before it returns here, which destroys
        // the cached Dialogue instance. Recover the same one-shot progression
        // that activated() performs after a normal cached return.
        if (
          Array.isArray(history) &&
          history.length > 0 &&
          !this.hasIncompleteTask &&
          !this.activeWorkflowPrompt
        ) {
          await this.getTreatmentNextFn({ silent: true });
        }
      }
    },
    async getTreatmentFn() {
      const resData = await this.getTreatment();
      if (resData) {
        this.scrollToBottom();
      }
      return resData;
    },
    async getTreatmentNextFn({ silent = false } = {}) {
      if (this.interactionBusy) {
        return false;
      }
      this.workflowAdvancing = true;
      try {
        return await this.claimWorkflowUntilInteractive({ silent });
      } finally {
        this.workflowAdvancing = false;
      }
    },
    async claimWorkflowUntilInteractive({ silent = false, maxAttempts = 12 } = {}) {
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (this.hasIncompleteTask || this.activeWorkflowPrompt) {
          return true;
        }
        const resData = await this.getTreatmentNext({ silent });
        if (!resData) {
          return false;
        }
        this.scrollToBottom();
      }
      return false;
    },
    async sendUserConfirmMsg(data) {
      if (this.interactionBusy) {
        return;
      }
      this.workflowAdvancing = true;
      try {
        await this.postUserConfirm({ id: data.id, text: data.text });
        if (data.source) {
          this.$set(data.source, "status", "COMPLETED");
        }
        this.appendTreatmentItems([{
          type: "ChatSend",
          text: data.text,
          isShowTime: true,
        }]);
        await this.claimWorkflowUntilInteractive();
      } catch (error) {
        this.$toast("暂时无法继续，请稍后重试");
      } finally {
        this.workflowAdvancing = false;
      }
    },
    async sendWorkflowReply() {
      const prompt = this.activeWorkflowPrompt;
      if (!prompt) {
        return;
      }
      await this.sendUserConfirmMsg(prompt);
    },
    async continueWorkflowAfterTask({ forceAdvance = false } = {}) {
      if (this.interactionBusy) {
        return;
      }
      const pendingBeforeRefresh = [...this.incompleteTaskIds];
      const resData = await this.getTreatmentFn();
      if (resData === false) {
        return;
      }
      const pendingAfterRefresh = new Set(this.incompleteTaskIds);
      const completedWhileAway = pendingBeforeRefresh.some(
        (id) => !pendingAfterRefresh.has(id)
      );
      if ((forceAdvance || completedWhileAway) && !this.hasIncompleteTask) {
        await this.getTreatmentNextFn({ silent: true });
      }
    },
    async sendText() {
      const text = this.trimmedInput;
      if (!text || this.interactionBusy) {
        return;
      }
      this.inputText = "";
      this.agentSending = true;
      this.appendTreatmentItems([{
        type: "ChatSend",
        text,
        isShowTime: true,
      }]);
      this.scrollToBottom();

      const sse = streamAgent(this.treatmentCode, this.treatmentInstanceId, {
        message: text,
        conversationId: this.conversationId,
        clientMessageId: uuidv4(),
      });
      sse.addEventListener("message", (event) => {
        let resJson = {};
        try {
          resJson = JSON.parse(event.data);
        } catch (error) {
          resJson = {};
        }
        if (resJson.code !== 200) {
          this.agentSending = false;
          this.$toast(resJson.msg || "Agent 响应失败");
          sse.close();
          return;
        }
        if (resJson.finished) {
          this.applyStructuredResult(resJson);
          this.agentSending = false;
          sse.close();
        }
      });
      sse.addEventListener("error", () => {
        this.agentSending = false;
        this.$toast("Agent 暂时不可用，请稍后再试");
        sse.close();
      });
      sse.stream();
    },
    applyStructuredResult(resJson) {
      const result = resJson.data || {};
      const assistantText = resJson.content || this.joinText(result.prefaceGuide, result.reply);
      const nextItems = [];
      if (assistantText) {
        nextItems.push({
          type: "ChatText",
          text: assistantText,
          item_type: "guide_language",
          cardMode: "dialogue",
          isShowTime: true,
        });
      }
      if (result.recommendedCard && !this.hasRenderedCard(result.recommendedCard)) {
        nextItems.push(this.convertAgentCard(result.recommendedCard));
      }
      this.appendTreatmentItems(nextItems);
      this.scrollToBottom();
    },
    appendTreatmentItems(items) {
      if (!items.length) {
        return;
      }
      this.$store.commit("setTreatmentList", [
        ...this.treatmentList,
        ...items,
      ]);
    },
    convertAgentCard(card) {
      const itemTypeList = this.$store.state.itemTypeList || {};
      return {
        id: card.id,
        item_type: card.itemType,
        type: itemTypeList[card.itemType] || "ChatAnswerSheet",
        settings: card.settings || {},
        cardMode: "dialogue",
        isShowTime: true,
        status: card.status || "INITIATED",
      };
    },
    hasRenderedCard(card) {
      return this.treatmentList.some((item) => (
        item &&
        item.id != null &&
        String(item.id) === String(card.id) &&
        item.item_type === card.itemType
      ));
    },
    joinText(prefaceGuide, reply) {
      if (!prefaceGuide) {
        return reply || "";
      }
      if (!reply) {
        return prefaceGuide;
      }
      return `${prefaceGuide}\n\n${reply}`;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.scrollableContainer) {
            this.$refs.scrollableContainer.scrollTop = this.$refs.scrollableContainer.scrollHeight;
          }
        }, 100);
      });
    },
  },
};
</script>

<style scoped lang="scss">
.dialogue {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.greeting-area {
  width: 100%;
  height: 103px;
  padding: 0 20px;
  box-sizing: border-box;
  background: #fff;
}

.dialogue-area {
  padding: 0 24px 18px;
  box-sizing: border-box;
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
}

.input-area {
  padding: 10px 16px calc(12px + env(safe-area-inset-bottom));
  background: rgba(255, 255, 255, 0.97);
  border-top: 1px solid #e8eef6;
  box-shadow: 0 -8px 24px rgba(64, 91, 128, 0.06);
}

.workflow-reply {
  width: 100%;
  min-height: 42px;
  margin-bottom: 9px;
  padding: 8px 12px;
  border: 1px solid #b9d9f4;
  border-radius: 12px;
  background: linear-gradient(135deg, #f2f8ff 0%, #e8f5ff 100%);
  color: #28425f;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 9px;
  text-align: left;
}

.workflow-reply:active {
  transform: translateY(1px);
}

.workflow-reply:disabled {
  opacity: 0.6;
}

.workflow-reply-label {
  padding: 3px 7px;
  border-radius: 8px;
  background: #d8ebfb;
  color: #49718f;
  font-size: 10px;
  line-height: 16px;
  white-space: nowrap;
}

.workflow-reply-text {
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 19px;
  overflow-wrap: anywhere;
}

.workflow-reply-arrow {
  color: #6d9cc2;
  font-size: 23px;
  line-height: 1;
}

.workflow-reminder {
  min-height: 38px;
  margin-bottom: 9px;
  padding: 9px 12px;
  box-sizing: border-box;
  border: 1px solid #d7e6f3;
  border-radius: 12px;
  background: #f5f9fc;
  color: #52718a;
  font-size: 12px;
  line-height: 18px;
}

.composer-row {
  display: flex;
  align-items: flex-end;
  gap: 9px;
}

.input-box {
  flex: 1;
  min-width: 0;
  height: 44px;
  min-height: 44px;
  max-height: 88px;
  padding: 11px 13px;
  box-sizing: border-box;
  border: 1px solid #dbe3ec;
  border-radius: 12px;
  background: #fbfcfe;
  resize: none;
  color: #26384b;
  font-size: 13px;
  line-height: 21px;
  outline: none;
}

.input-box:focus {
  border-color: #86bde7;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(134, 189, 231, 0.14);
}

.input-box::placeholder {
  color: #9aa8b6;
}

.send-btn {
  flex: 0 0 64px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #4f88d8 0%, #3972c4 100%);
  box-shadow: 0 5px 12px rgba(57, 114, 196, 0.2);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
}

.send-btn:disabled {
  background: #cbd5df;
  box-shadow: none;
  opacity: 0.8;
}

.quick-reply-enter-active,
.quick-reply-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.quick-reply-enter,
.quick-reply-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
